import React, { useMemo, useState, useEffect, useLayoutEffect, useRef, useCallback, startTransition } from "react";
import { Box, Button, Text, VStack, Heading, HStack, ButtonGroup, Tooltip, IconButton, useDisclosure } from "@chakra-ui/react";
import styles from "../Ace.module.css";
import { connect } from "react-redux";
import { Resize1, Resize2 } from "../icons";
import { ChevronDownIcon, ChevronRightIcon, ViewIcon } from "@chakra-ui/icons";
import { removeLines, shouldRemoveHiddenLine } from "../ReusableFunctions";
import { applyAcceptToEditor, cleanupItem } from '../SuggestionAction';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import UseSetAllFiles from "../Customhook/UseSetAllFiles";
import { acceptItem, rejectAll, rejectItem, removeFromGlobalAnchorList } from "../AutoSuggestion";
import { globalAnchorList } from "../AutoSuggestion";


const VIRTUALIZE_THRESHOLD = 40;
const ITEM_HEIGHT = 92;
const GROUP_MAX_HEIGHT = 500;


const VirtualGroupList = ({ items, renderItem, scrollToId, onMount }) => {
  const containerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [boxHeight, setBoxHeight] = useState(GROUP_MAX_HEIGHT);

  useLayoutEffect(() => {
    if (containerRef.current) {
      setBoxHeight(containerRef.current.clientHeight);
      onMount?.(containerRef.current);
    }
  }, []);

  useEffect(() => {
    if (scrollToId && containerRef.current) {
      const index = items.findIndex(i => i.id === scrollToId);
      if (index !== -1) {
        const targetScrollY = index * ITEM_HEIGHT;
        containerRef.current.scrollTo({ top: targetScrollY, behavior: 'smooth' });
        setScrollTop(targetScrollY);
      }
    }
  }, [scrollToId, items]);

  const totalHeight = items.length * ITEM_HEIGHT;
  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - 3);
  const visibleCount = Math.ceil(boxHeight / ITEM_HEIGHT) + 6; // +6 overscan
  const endIndex = Math.min(items.length - 1, startIndex + visibleCount);
  const visibleItems = items.slice(startIndex, endIndex + 1);
  const offsetY = startIndex * ITEM_HEIGHT;

  return (
    <div
      ref={containerRef}
      onScroll={e => setScrollTop(e.currentTarget.scrollTop)}
      style={{
        height: `${Math.min(totalHeight, GROUP_MAX_HEIGHT)}px`,
        overflowY: "auto", overflowX: "hidden", position: "relative",
        scrollbarWidth: "thin", scrollbarColor: "#bbb #f5f5f5",
      }}
    >
      <div style={{ height: `${totalHeight}px`, position: "relative" }}>
        <div style={{ position: "absolute", top: `${offsetY}px`, width: "100%" }}>
          {visibleItems.map((item, idx) =>
            renderItem(item, startIndex + idx)
          )}
        </div>
      </div>
    </div>
  );
};


const EditCorrections = (props) => {
  const [openGroups, setOpenGroups] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("all");
  const { Allfiles, setAllFiles } = UseSetAllFiles();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activeDiffData, setActiveDiffData] = useState({ old: "", new: "", title: "" });

  const [highlightedItemId, setHighlightedItemId] = useState(null);
  const itemRefs = useRef({});
  useEffect(() => {
    const handleNavigate = (e) => {
      const item = e.detail;
      if (!item) return;

      const styleName = item.isAI ? "AI Suggestions" : (item.styleInfo?.name || "General");
      setOpenGroups(prev => ({ ...prev, [styleName]: true }));


      setHighlightedItemId(item.id);
      setTimeout(() => setHighlightedItemId(null), 7000); // Clear after 2.5s
      setTimeout(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        if (itemRefs.current[item.id]) {
          scrollToInContainer(itemRefs.current[item.id]);
        } else if (groupHeaderRefs.current[styleName]) {
          scrollToInContainer(groupHeaderRefs.current[styleName]);
        }
      }, 120);
    };

    window.addEventListener('NAVIGATE_TO_SIDEBAR_SUGGESTION', handleNavigate);
    return () => window.removeEventListener('NAVIGATE_TO_SIDEBAR_SUGGESTION', handleNavigate);
  }, []);
  // ── Scroll preservation ──
  const scrollContainerRef = useRef(null);
  const savedScrollTop = useRef(0);
  const groupHeaderRefs = useRef({});
  const groupListRefs = useRef({});

  const scrollToInContainer = useCallback((targetEl) => {
    const container = scrollContainerRef.current;
    if (!targetEl || !container) return;
    const containerRect = container.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const newScrollTop = container.scrollTop + targetRect.top - containerRect.top - 8;
    container.scrollTo({ top: Math.max(0, newScrollTop), behavior: 'smooth' });
  }, []);

  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      savedScrollTop.current = scrollContainerRef.current.scrollTop;
    }
  }, []);

  // Restore scroll after every render, but only when NOT in initial-load state
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (savedScrollTop.current > 0) {
      el.scrollTop = savedScrollTop.current;
    }
  }); // intentionally no deps — runs after every render

  const toggleGroup = (name) => {
    setOpenGroups((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };
  console.log("the data in the :", props.autoSuggestion)

  const moveToPosition = (item) => {
    const editor = props.ace.current.editor;
    editor.session.selection.setRange({
      start: { row: item.startAnchor.row, column: item.startAnchor.column },
      end: { row: item.endAnchor.row, column: item.endAnchor.column },
    });
    editor.scrollToLine(item.startAnchor.row, true, true);
    editor.focus();
  };

  const MultiSuggestionCard = ({ item, onAccept, onReject, onMove }) => {
    const [chosenIndex, setChosenIndex] = React.useState(
      item.extraStyleInfo?.chosenIndex ?? 0
    );
    const options = item.extraStyleInfo?.multiSuggestions ?? [];

    const handleAccept = (e) => {
      e.stopPropagation();

      const chosen = options[chosenIndex];
      if (!chosen) return;

      const itemWithChoice = {
        ...item,
        suggestionText: chosen.suggestionText,
        extraStyleInfo: {
          ...item.extraStyleInfo,
          chosenIndex,
        },
      };

      onAccept(itemWithChoice);
    };

    return (
      <Box
        p="4" paddingRight="32px"
        display="flex" flexDirection="column" gap="12px"
        cursor="pointer"
        onClick={() => onMove(item)}
        style={{ borderBottom: '1px solid #eee' }}
        _hover={{ backgroundColor: '#f9f9f9' }}
      >
        <Text fontSize="14px" color="#333333" fontWeight="600">
          {item.styleInfo?.description}
        </Text>
        <Text fontSize="12px" color="gray.500">
          Line {item.startAnchor.row + 1} — select one statement to insert:
        </Text>

        <Box display="flex" flexDirection="column" gap="8px" onClick={e => e.stopPropagation()}>
          {options.map((opt, idx) => (
            <Box
              key={idx}
              display="flex" alignItems="flex-start" gap="8px"
              padding="8px 10px"
              borderRadius="6px"
              border={`1.5px solid ${chosenIndex === idx ? '#3498db' : '#e2e8f0'}`}
              backgroundColor={chosenIndex === idx ? '#e3f2fd' : '#ffffff'}
              cursor="pointer"
              onClick={(e) => { e.stopPropagation(); setChosenIndex(idx); }}
            >
              <Box
                flexShrink={0}
                mt="2px"
                width="14px" height="14px"
                borderRadius="full"
                border="2px solid"
                borderColor={chosenIndex === idx ? '#3498db' : '#cbd5e0'}
                backgroundColor={chosenIndex === idx ? '#3498db' : 'transparent'}
              />
              <Box flex="1">
                <Text fontSize="12px" fontWeight="600" color="#3498db" mb="2px">
                  {opt.label}
                </Text>
                <Text fontSize="12px" color="#444" lineHeight="1.5" wordBreak="break-word">
                  {opt.text}
                </Text>
              </Box>
            </Box>
          ))}
        </Box>

        <HStack justifyContent="flex-end" gap="8px" onClick={e => e.stopPropagation()}>
          <Button
            size="sm" variant="outline"
            border="1px solid #DDDDDD" borderRadius="20px" padding="7px"
            onClick={handleAccept}
            _hover={{ backgroundColor: '#e8f5e9' }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="#208923"
                strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <Text ml="4px" fontSize="13px" color="#208923">Accept</Text>
          </Button>
          <Button
            size="sm" variant="outline"
            border="1px solid #DDDDDD" borderRadius="20px" padding="7px"
            onClick={(e) => { e.stopPropagation(); onReject(item); }}
            _hover={{ backgroundColor: '#ffebee' }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="#E30F0F"
                strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <Text ml="4px" fontSize="13px" color="#E30F0F">Reject</Text>
          </Button>
        </HStack>
      </Box>
    );
  };

  // const insertQuery = (item) => {
  //   if (!item || !item.startAnchor) return;
  //   const editor = props.ace.current.editor;
  //   const session = editor.session;
  //   try {
  //     const row = item.startAnchor.row;
  //     const styleName = item.styleInfo?.description || "Query";
  //     const queryText = `\\AQ{${styleName}}\n`;
  //     session.insert({ row, column: 0 }, queryText);
  //     editor.gotoLine(row + 1, 0);
  //     editor.scrollToLine(row, true, true);
  //     if (props.setTextFile) props.setTextFile(session.getValue());
  //   } catch (error) {
  //     console.error("Error inserting query:", error);
  //   }
  // };
  const insertQuery = (item) => {
    if (!item) return;
    const editor = props.ace.current.editor;
    const session = editor.session;
    try {
      // 1. Get the current cursor position in the editor
      const cursorPos = editor.getCursorPosition();

      // 2. Get the query text
      const styleName = item.styleInfo?.description || "Query";

      // 3. Format the tag (Removed the \n so it inserts seamlessly inline)
      const queryText = `\\AQ{${styleName}}`;

      // 4. Insert exactly at the cursor coordinates
      session.insert(cursorPos, queryText);

      // 5. Refocus the editor so the user can immediately keep typing
      editor.focus();

      // 6. Update the global state
      if (props.setTextFile) props.setTextFile(session.getValue());
    } catch (error) {
      console.error("Error inserting query:", error);
    }
  };

  const acceptSuggestion = (item) => {
    if (!item?.startAnchor || !item?.endAnchor) return;

    if (item.extraStyleInfo?.isMultiSuggestion) {
      const idx = item.extraStyleInfo.chosenIndex ?? 0;
      const chosen = item.extraStyleInfo.multiSuggestions?.[idx];
      if (chosen) {
        item.suggestionText = chosen.suggestionText;
      }
    }

    acceptItem(props.ace.current.editor, item, props.setTextFile, props, setAllFiles);
  };

  const rejectSuggestion = (item) => {
    if (!item?.startAnchor) return;
    const editor = props.ace.current.editor;
    rejectItem(editor, item, props);
  };

  const acceptAllSuggestions = (items) => {
    [...items].reverse().forEach(item => acceptSuggestion(item));
  };

  const rejectAllSuggestions = (items) => {
    [...items].reverse().forEach(item => rejectSuggestion(item));
  };

  const handleOpenDiff = (e, item) => {
    e.stopPropagation();

    const editor = props.ace.current.editor;
    const session = editor.session;
    const totalLines = session.getLength();

    const contextCount = 2;
    const startContextRow = Math.max(0, item.startAnchor.row - contextCount);
    const endContextRow = Math.min(totalLines - 1, item.endAnchor.row + contextCount);

    let prefixRows = "";
    if (item.startAnchor.row > startContextRow) {
      prefixRows = session.getLines(startContextRow, item.startAnchor.row - 1).join("\n") + "\n";
    }

    let suffixRows = "";
    if (item.endAnchor.row < endContextRow) {
      suffixRows = "\n" + session.getLines(item.endAnchor.row + 1, endContextRow).join("\n");
    }

    const lineHead = session.getLine(item.startAnchor.row).slice(0, item.startAnchor.column);
    const lineTail = session.getLine(item.endAnchor.row).slice(item.endAnchor.column);

    const oldDisplay = `${prefixRows}${lineHead}${item.originalText}${lineTail}${suffixRows}`;
    const newDisplay = `${prefixRows}${lineHead}${item.suggestionText || ""}${lineTail}${suffixRows}`;

    setActiveDiffData({
      old: oldDisplay,
      new: newDisplay,
      title: item.styleInfo?.name || "Suggestion Review"
    });

    onOpen();
  };

  const handleClearAll = () => {
    const editor = props.ace.current.editor;
    const session = editor.session;
    const allSuggestions = [...(props.autoSuggestions || [])];
    [...allSuggestions].reverse().forEach(item => rejectSuggestion(item));

    props.editToolRejected();
    props.NoSuggestions();
    props.clearAutoSuggestions();
    props.clearAutoSuggestionRejects();
    props.triggerClearAll();
    rejectAll();
  };

  // const filteredSuggestions = useMemo(() => {
  //   const suggestions = props.autoSuggestions || [];
  //   const rejected = props.autoSuggestionsRejected || [];
  //   const rejectedIds = new Set(rejected.map(r => r.id));
  //   return suggestions.filter(item => !rejectedIds.has(item.id));
  // }, [props.autoSuggestions, props.autoSuggestionsRejected]);

  const filteredSuggestions = useMemo(() => {
    const suggestions = props.autoSuggestions || [];
    const rejected = props.autoSuggestionsRejected || [];
    const rejectedIds = new Set(rejected.map(r => r.id));
    return suggestions.filter(item =>
      !rejectedIds.has(item.id) &&
      item.styleInfo?.name !== "SpellCheck"  // Hide this style completely
    );
  }, [props.autoSuggestions, props.autoSuggestionsRejected]);


  const groupedSuggestions = useMemo(() => {
    const groups = filteredSuggestions.reduce((acc, item) => {
      const styleName = item.isAI ? "AI Suggestions" : (item.styleInfo.name || "General");
      if (!acc[styleName]) acc[styleName] = [];
      acc[styleName].push(item);
      return acc;
    }, {});

    const sortedEntries = Object.entries(groups).sort(([a], [b]) => {
      if (a === "AI Suggestions") return -1;
      if (b === "AI Suggestions") return 1;
      return a.localeCompare(b);
    });

    return Object.fromEntries(sortedEntries);
  }, [filteredSuggestions]);

  const filteredGroupedSuggestions = useMemo(() => {
    if (!searchQuery.trim()) return groupedSuggestions;
    const q = searchQuery.toLowerCase();
    return Object.fromEntries(
      Object.entries(groupedSuggestions).filter(([styleName]) => styleName.toLowerCase().includes(q))
    );
  }, [groupedSuggestions, searchQuery]);

  const displayedSuggestions = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filteredGroupedSuggestions).filter(([, items]) => {
        if (viewMode === "queries") return items[0]?.queryOnly === true;
        if (viewMode === "styles") return !items[0]?.queryOnly;
        return true;
      })
    );
  }, [filteredGroupedSuggestions, viewMode]);

  const isEmpty = Object.keys(displayedSuggestions).length === 0;
  const TRUNCATE_LIMIT = 40;

  const TruncatedText = ({ text, label, color }) => {
    const [expanded, setExpanded] = useState(false);
    const displayText = text === " " ? "<space>" : text?.replace(/\n/g, "\\n");
    const isTruncatable = displayText.length > TRUNCATE_LIMIT;

    return (
      <Heading fontSize="15px" display="flex" gap="4px" color="#333333" width="100%" whiteSpace="pre-wrap">
        <span style={{ color, fontWeight: "600", flexShrink: 0 }}>{label} </span>
        <span style={{ wordBreak: "break-all", fontWeight: "600" }}>
          {isTruncatable && !expanded ? (
            <>
              {displayText.slice(0, TRUNCATE_LIMIT)}
              <span
                onClick={(e) => { e.stopPropagation(); setExpanded(true); }}
                style={{ color: "#3498db", cursor: "pointer", fontWeight: "500" }}
              >
                ...
              </span>
            </>
          ) : (
            <>
              {displayText}
              {isTruncatable && (
                <span
                  onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
                  style={{ color: "#3498db", cursor: "pointer", fontWeight: "500", marginLeft: "4px" }}
                >
                  {" "}show less
                </span>
              )}
            </>
          )}
        </span>
      </Heading>
    );
  };



  const UKtoUSTable = ({ items, onAcceptAll, onRejectAll, onRowClick, fromLabel = "UK Words", toLabel = "Suggested US Words", showMajorityCount = false }) => {
    const wordMap = {};
    items.forEach(item => {
      const key = item.originalText?.toLowerCase();
      if (!key) return;
      if (!wordMap[key]) {
        wordMap[key] = {
          ukWord: item.originalText,
          usWord: item.suggestionText,
          count: 0,
          matchedItems: [],
          majorityCount: item.extraStyleInfo?.majorityCount ?? 0,
        };
      }
      wordMap[key].count += 1;
      wordMap[key].matchedItems.push(item);
    });

    const majorityCounts = {};
    items.forEach(item => {
      const sug = item.suggestionText?.toLowerCase();
      if (!sug) return;
      majorityCounts[sug] = (majorityCounts[sug] || 0) + 1;
    });

    const rows = Object.values(wordMap);

    return (
      <Box p="4">
        <Box
          as="table"
          width="100%"
          style={{ borderCollapse: "collapse", fontSize: "14px" }}
        >
          <Box as="thead">
            <Box as="tr" backgroundColor="#f5f5f5">
              <Box as="th" style={{ textAlign: "left", padding: "10px 14px", borderBottom: "2px solid #e2e8f0", color: "#555", fontWeight: "600", fontSize: "13px" }}>
                {fromLabel}
              </Box>
              <Box as="th" style={{ textAlign: "center", padding: "10px 14px", borderBottom: "2px solid #e2e8f0", color: "#555", fontWeight: "600", fontSize: "13px" }}>
                Count
              </Box>
              <Box as="th" style={{ textAlign: "left", padding: "10px 14px", borderBottom: "2px solid #e2e8f0", color: "#555", fontWeight: "600", fontSize: "13px" }}>
                {toLabel}
              </Box>
              {showMajorityCount && (
                <Box as="th" style={{ textAlign: "center", padding: "10px 14px", borderBottom: "2px solid #e2e8f0", color: "#555", fontWeight: "600", fontSize: "13px" }}>
                  Count
                </Box>
              )}
              <Box as="th" style={{ textAlign: "center", padding: "10px 14px", borderBottom: "2px solid #e2e8f0", color: "#555", fontWeight: "600", fontSize: "13px" }}>
                Actions
              </Box>
            </Box>
          </Box>
          <Box as="tbody">
            {rows.map((row, idx) => (
              <Box
                as="tr"
                key={idx}
                _hover={{ backgroundColor: "#fafafa" }}
                style={{ borderBottom: "1px solid #eee" }}
                onClick={() => onRowClick && onRowClick(row.matchedItems[0])}
              >
                <Box as="td" style={{ padding: "10px 14px", color: "#E30F0F", fontWeight: "600" }}>
                  {row.ukWord}
                </Box>
                <Box as="td" style={{ padding: "10px 14px", textAlign: "center", color: "#333", fontWeight: "500" }}>
                  {row.count}
                </Box>
                <Box as="td" style={{ padding: "10px 14px", color: "#208923", fontWeight: "600" }}>
                  {row.usWord}
                </Box>
                {showMajorityCount && (
                  <Box as="td" style={{ padding: "10px 14px", textAlign: "center", color: "#333", fontWeight: "500" }}>
                    {row.majorityCount || 0}
                  </Box>
                )}
                <Box as="td" style={{ padding: "10px 14px", textAlign: "center" }}>
                  <HStack gap="6px" justifyContent="center">

                    <Button
                      size="sm" variant="outline"
                      border="1px solid #DDDDDD" borderRadius="20px" padding="7px"
                      onClick={() => onAcceptAll(row.matchedItems)}
                      _hover={{ backgroundColor: "#e8f5e9" }}
                      title="Accept all occurrences"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="#208923" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Button>

                    <Button
                      size="sm" variant="outline"
                      border="1px solid #DDDDDD" borderRadius="20px" padding="7px"
                      onClick={() => onRejectAll(row.matchedItems)}
                      _hover={{ backgroundColor: "#ffebee" }}
                      title="Reject all occurrences"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M12 4L4 12M4 4L12 12" stroke="#E30F0F" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </Button>
                  </HStack>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };
  // const DynamicTable = ({ items, columns, onAcceptAll, onRejectAll, onRowClick }) => {
  //   // Group by originalText
  //   const wordMap = {};
  //   items.forEach(item => {
  //     const key = item.originalText?.toLowerCase();
  //     if (!key) return;
  //     if (!wordMap[key]) {
  //       wordMap[key] = {
  //         originalText: item.originalText,
  //         suggestionText: item.suggestionText,
  //         count: 0,
  //         majorityCount: item.extraStyleInfo?.majorityCount ?? 0,
  //         matchedItems: [],
  //       };
  //     }
  //     wordMap[key].count += 1;
  //     wordMap[key].matchedItems.push(item);
  //   });

  //   const rows = Object.values(wordMap);

  //   const dedupedRows = rows.filter(row => {
  //     const sugKey = row.suggestionText?.toLowerCase();
  //     const reverseRow = wordMap[sugKey];
  //     if (!reverseRow) return true; // no circular pair → show

  //     // Both exist → keep only the true minority (lower minorityCount)
  //     const myCount = row.matchedItems[0]?.extraStyleInfo?.minorityCount ?? row.count;
  //     const theirCount = reverseRow.matchedItems[0]?.extraStyleInfo?.minorityCount ?? reverseRow.count;
  //     return myCount <= theirCount; // tie-ஆனா both hide (equal shouldn't happen)
  //   });


  //   const getCellValue = (row, col) => {
  //     switch (col.key) {
  //       case "originalText": return row.originalText;
  //       case "suggestionText": return row.suggestionText;
  //       case "count": return row.count;
  //       case "majorityCount": return row.matchedItems[0]?.extraStyleInfo?.majorityCount ?? 0;
  //       case "minorityCount": return row.matchedItems[0]?.extraStyleInfo?.minorityCount ?? row.count;
  //       default: return row[col.key] ?? "-";
  //     }
  //   };

  //   return (
  //     <Box p="4">
  //       <Box as="table" width="100%" style={{ borderCollapse: "collapse", fontSize: "14px" }}>
  //         <Box as="thead">
  //           <Box as="tr" backgroundColor="#f5f5f5">
  //             {columns.map((col, i) => (
  //               <Box
  //                 key={i} as="th"
  //                 style={{
  //                   textAlign: col.align || "left",
  //                   padding: "10px 14px",
  //                   borderBottom: "2px solid #e2e8f0",
  //                   color: "#555", fontWeight: "600", fontSize: "13px"
  //                 }}
  //               >
  //                 {col.label}
  //               </Box>
  //             ))}

  //             <Box as="th" style={{ textAlign: "center", padding: "10px 14px", borderBottom: "2px solid #e2e8f0", color: "#555", fontWeight: "600", fontSize: "13px" }}>
  //               Actions
  //             </Box>
  //           </Box>
  //         </Box>

  //         <Box as="tbody">
  //           {dedupedRows.map((row, idx) => (
  //             <Box
  //               as="tr" key={idx}
  //               _hover={{ backgroundColor: "#fafafa" }}
  //               style={{ borderBottom: "1px solid #eee", cursor: "pointer" }}
  //               onClick={() => onRowClick?.(row.matchedItems[0])}
  //             >
  //               {columns.map((col, i) => (
  //                 <Box
  //                   key={i} as="td"
  //                   style={{
  //                     padding: "10px 14px",
  //                     textAlign: col.align || "left",
  //                     color: col.color || "#333",
  //                     fontWeight: col.color ? "600" : "500",
  //                   }}
  //                 >
  //                   {getCellValue(row, col)}
  //                 </Box>
  //               ))}
  //               {/* Actions */}
  //               <Box as="td" style={{ padding: "10px 14px", textAlign: "center" }}>
  //                 <HStack gap="6px" justifyContent="center">
  //                   <Button
  //                     size="sm" variant="outline"
  //                     border="1px solid #DDDDDD" borderRadius="20px" padding="7px"
  //                     onClick={(e) => { e.stopPropagation(); onAcceptAll(row.matchedItems); }}
  //                     _hover={{ backgroundColor: "#e8f5e9" }}
  //                     title="Accept all"
  //                   >
  //                     <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
  //                       <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="#208923" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  //                     </svg>
  //                   </Button>
  //                   <Button
  //                     size="sm" variant="outline"
  //                     border="1px solid #DDDDDD" borderRadius="20px" padding="7px"
  //                     onClick={(e) => { e.stopPropagation(); onRejectAll(row.matchedItems); }}
  //                     _hover={{ backgroundColor: "#ffebee" }}
  //                     title="Reject all"
  //                   >
  //                     <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
  //                       <path d="M12 4L4 12M4 4L12 12" stroke="#E30F0F" strokeWidth="1.5" strokeLinecap="round" />
  //                     </svg>
  //                   </Button>
  //                 </HStack>
  //               </Box>
  //             </Box>
  //           ))}
  //         </Box>
  //       </Box>
  //     </Box>
  //   );
  // };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent maxWidth="95vw">
          <ModalHeader borderBottom="1px solid #eee">
            Review Changes: {activeDiffData.title}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0}>
            <Box height="70vh" overflowY="auto">
              <ReactDiffViewer
                oldValue={activeDiffData.old}
                newValue={activeDiffData.new}
                splitView={true}
                compareMethod={DiffMethod.CHARS}
                leftTitle="Original (with context)"
                rightTitle="Suggested (with context)"
                styles={{
                  line: { fontSize: '13px', lineHeight: '22px', wordBreak: 'break-all', whiteSpace: 'pre-wrap' },
                  contentText: { wordBreak: 'break-all' }
                }}
              />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      <div className={styles.header}>
        <HStack
          backgroundColor="var(--primary-main)"
          justifyContent="space-between"
          h="48px"
          width="auto"
          zIndex={10}
        >
          <ButtonGroup spacing="1">
            <Button color="white" background="black" size="lg" zIndex={10} colorScheme="#f47c0d" aria-label="Suggestions">
              Suggestions ({filteredSuggestions.length})
            </Button>
            <Box display="flex" position={"absolute"} right={0}>
              <Button
                onClick={handleClearAll}
                color="white" size="lg" colorScheme="#f47c0d" aria-label="Clear All"
                _hover={{ bg: "var(--secondary-main)" }}
              >
                Clear All
              </Button>
              {props.pdfFull ? (
                <Tooltip label="Minimize">
                  <IconButton onClick={props.toggleEditor} size="lg" bg="var(--primary-main)"
                    aria-label="Resize" icon={<Resize2 />} _hover={{ bg: "var(--secondary-main)" }} />
                </Tooltip>
              ) : (
                <Tooltip label="Maximize">
                  <IconButton onClick={props.toggleEditor} size="lg" colorScheme="#f47c0d"
                    aria-label="Resize" icon={<Resize1 />} bg="var(--primary-main)"
                    _hover={{ bg: "var(--secondary-main)" }} />
                </Tooltip>
              )}
            </Box>
          </ButtonGroup>
        </HStack>
      </div>

      <Box px="4" py="2" borderBottom="1px solid #e2e8f0" backgroundColor="white">
        <HStack gap="8px">
          <input
            aria-label="Search styles"
            type="text"
            placeholder="Search styles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1, padding: "8px 12px", border: "1px solid #e2e8f0",
              borderRadius: "8px", fontSize: "14px", outline: "none",
              color: "#333", backgroundColor: "#f9f9f9",
            }}
            onFocus={(e) => e.target.style.borderColor = "#3498db"}
            onBlur={(e) => e.target.style.borderColor = "#e2e8f0"}
          />
          <select
            aria-label="Filter by type"
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            style={{
              padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: "8px",
              fontSize: "14px", outline: "none", color: "#333",
              backgroundColor: "#f9f9f9", cursor: "pointer", minWidth: "90px",
            }}
            onFocus={(e) => e.target.style.borderColor = "#3498db"}
            onBlur={(e) => e.target.style.borderColor = "#e2e8f0"}
          >
            <option value="all">All</option>
            <option value="queries">Queries</option>
            <option value="styles">Styles</option>
          </select>
        </HStack>
      </Box>

      {props.isLoadingSuggestions ? (
        <div style={{ flexShrink: 0 }}>
          <div style={{
            height: "3px",
            background: "linear-gradient(90deg, var(--primary-main) 0%, #f47c0d 50%, var(--primary-main) 100%)",
            backgroundSize: "200% 100%",
            animation: "loadingBar 1.2s linear infinite",
          }} />
          <div style={{
            padding: "6px 0", textAlign: "center", fontSize: "13px",
            fontWeight: "500", color: "rgba(0,0,0,0.5)",
            backgroundColor: "#fffdf9", borderBottom: "1px solid #f0e8e0",
          }}>
            Loading suggestions...
          </div>
        </div>
      ) : (
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          style={{
            overflowY: "auto", overflowX: "hidden", height: "84vh",
            display: "flex", flexDirection: "column", gap: 0,
            scrollbarWidth: "thin", scrollbarColor: "#888 #f1f1f1", paddingBottom: "50px",
          }}
        >
          {!isEmpty ? (
            Object.entries(displayedSuggestions).map(([styleName, items]) => (
              <div key={styleName} style={{ width: "100%" }}>
                <Box borderRadius="md">

                  <HStack
                    ref={el => { groupHeaderRefs.current[styleName] = el; }}
                    backgroundColor="#F5F5F5"
                    justifyContent="space-between"
                    alignItems="center"
                    borderBottom="1px solid #e2e8f0"
                    px="3" py="3"
                    cursor="pointer"
                    onClick={() => toggleGroup(styleName)}
                    _hover={{ backgroundColor: "#EEEEEE" }}
                    position="sticky"
                    top="0"
                    zIndex={2}
                  >
                    <HStack gap="2" alignItems="center">
                      {openGroups[styleName]
                        ? <ChevronDownIcon w={5} h={5} color="black" />
                        : <ChevronRightIcon w={5} h={5} color="black" />
                      }
                      <Text
                        fontSize="16px" fontWeight="medium"
                        color={styleName === "AI Suggestions" ? "blue.600" : "black"}
                      >
                        {styleName}
                      </Text>
                    </HStack>

                    <HStack gap="8px" paddingRight="20px" onClick={(e) => e.stopPropagation()}>
                      <Text fontSize="14px" color="gray.600" fontWeight="normal">
                        ({items.length})
                      </Text>

                      {(() => {
                        const timed = items.find(i => i.styleDurationMs !== undefined);
                        if (!timed) return null;
                        const ms = timed.styleDurationMs;
                        const label = ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
                        const bg = ms < 100 ? "#e8f5e9" : ms < 500 ? "#fff8e1" : "#fce4e4";
                        const color = ms < 100 ? "#2e7d32" : ms < 500 ? "#e65100" : "#c62828";
                        return (
                          <Box
                            display="inline-flex" alignItems="center" gap="4px"
                            px="7px" py="2px" borderRadius="full"
                            fontSize="11px" fontWeight="600"
                            bg={bg} color={color}
                            title={`Style "${styleName}" completed in ${ms}ms`}
                          >
                            <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                              <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
                              <path d="M8 5v3.5l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            {label}
                          </Box>
                        );
                      })()}

                      {!items[0]?.queryOnly && (
                        <>
                          <Button
                            display="flex" gap="8px" borderRadius="8px" padding="8px 10px"
                            variant="outline" backgroundColor="white" border="1px solid #E0DBD7"
                            size="sm" color="#504C4A"
                            onClick={(e) => { e.stopPropagation(); acceptAllSuggestions(items); }}
                            _hover={{ backgroundColor: "#f0f0f0" }}
                          >
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="#208923" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Accept All
                          </Button>
                          <Button
                            variant="outline" display="flex" gap="8px" borderRadius="8px" padding="8px 10px"
                            backgroundColor="white" border="1px solid #E0DBD7" size="sm" color="#504C4A"
                            onClick={(e) => { e.stopPropagation(); rejectAllSuggestions(items); }}
                            _hover={{ backgroundColor: "#f0f0f0" }}
                          >
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path d="M12 4L4 12" stroke="#E30F0F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M4 4L12 12" stroke="#E30F0F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Reject All
                          </Button>
                        </>
                      )}
                    </HStack>
                  </HStack>

                  {openGroups[styleName] && (() => {

                    // if (items[0]?.styleInfo?.tags === "table") {
                    //   const isUKtoUS = styleName === "Convert UK Word to US Word";
                    //   const isUStoUK = styleName === "Convert US Word to UK Word";
                    //   const isMajority = styleName === "Convert To Majority Spelling";

                    //   return <UKtoUSTable
                    //     items={items}
                    //     onAcceptAll={(matchedItems) => acceptAllSuggestions(matchedItems)}
                    //     onRejectAll={(matchedItems) => rejectAllSuggestions(matchedItems)}
                    //     fromLabel={isUKtoUS ? "UK Words" : isUStoUK ? "US Words" : "Minority Words"}
                    //     toLabel={isUKtoUS ? "Suggested US Words" : isUStoUK ? "Suggested UK Words" : "Majority Words"}
                    //     onRowClick={(item) => moveToPosition(item)}
                    //     showMajorityCount={isMajority}
                    //   />
                    // }

                    if (items[0]?.styleInfo?.tags === "table") {
                      const tableConfig = items[0]?.extraStyleInfo?.tableConfig ?? {};
                      const isMajority = styleName === "Convert To Majority Spelling";

                      return <UKtoUSTable
                        items={items}
                        onAcceptAll={(matchedItems) => acceptAllSuggestions(matchedItems)}
                        onRejectAll={(matchedItems) => rejectAllSuggestions(matchedItems)}
                        fromLabel={tableConfig.fromLabel ?? "Original Words"}
                        toLabel={tableConfig.toLabel ?? "Suggested Words"}
                        onRowClick={(item) => moveToPosition(item)}
                        showMajorityCount={isMajority}
                      />
                    }

                    // if (items[0]?.extraStyleInfo?.tags === "table") {
                    //   const tableConfig = items[0]?.extraStyleInfo?.tableConfig;
                    //   const columns = tableConfig?.columns ?? [
                    //     { key: "originalText", label: "Original", color: "#E30F0F", align: "left" },
                    //     { key: "count", label: "Count", align: "center" },
                    //     { key: "suggestionText", label: "Suggested", color: "#208923", align: "left" },
                    //   ];
                    //   return (
                    //     <DynamicTable
                    //       items={items}
                    //       columns={columns}
                    //       onAcceptAll={(matchedItems) => acceptAllSuggestions(matchedItems)}
                    //       onRejectAll={(matchedItems) => rejectAllSuggestions(matchedItems)}
                    //       onRowClick={(item) => moveToPosition(item)}
                    //     />
                    //   );
                    // }

                    const renderItem = (item, i) => {
                      if (item.extraStyleInfo?.isMultiSuggestion) {
                        return (
                          <MultiSuggestionCard
                            key={item.id ?? i}
                            item={item}
                            onAccept={acceptSuggestion}
                            onReject={rejectSuggestion}
                            onMove={moveToPosition}
                          />
                        );
                      }

                      return (
                        <Box
                          key={item.id ?? i} p="4" paddingRight="32px"
                          ref={el => { itemRefs.current[item.id] = el; }}
                          backgroundColor={highlightedItemId === item.id ? "#e3f2fd" : "transparent"}
                          transition="background-color 0.5s ease"

                          display="flex" justifyContent="space-between" alignItems="center"
                          gap="20px" cursor="pointer"
                          onClick={() => moveToPosition(item)}
                          style={{ borderBottom: "1px solid #eee" }}
                          _hover={{ backgroundColor: highlightedItemId === item.id ? "#e3f2fd" : "#f9f9f9" }}
                        >
                          <Box width="calc(100% - 130px)">
                            {item.queryOnly ? (
                              <>
                                <Text fontSize="14px" color="#333333" fontWeight="500">
                                  {item.styleInfo?.description}
                                </Text>
                                <Box display="flex" alignItems="center" gap="8px" mt="6px" flexWrap="wrap">
                                  <Text fontSize="12px" color="gray.500">
                                    Line {item.startAnchor.row + 1}, Column {item.startAnchor.column + 1}
                                  </Text>
                                </Box>
                              </>
                            ) : (
                              <>
                                <TruncatedText
                                  text={item.originalText}
                                  label="Original:"
                                  color="#E30F0F"
                                />
                                {item.suggestionText && item.suggestionText.trim() && (
                                  <TruncatedText
                                    text={item.suggestionText}
                                    label="Suggested:"
                                    color="#208923"
                                  />
                                )}
                                <Box display="flex" alignItems="center" gap="8px" mt="6px" flexWrap="wrap">
                                  <Text fontSize="12px" color="gray.500">
                                    Line {item.startAnchor.row + 1}, Column {item.startAnchor.column + 1}
                                  </Text>
                                </Box>
                              </>
                            )}
                          </Box>

                          <HStack minWidth="180px" justifyContent="flex-end" gap="6px" onClick={(e) => e.stopPropagation()}>
                            {!item.queryOnly && (
                              <>
                                <Tooltip label="View Context Diff">
                                  <IconButton
                                    size="sm" icon={<ViewIcon />}
                                    variant="outline" colorScheme="blue"
                                    borderRadius="full"
                                    onClick={(e) => handleOpenDiff(e, item)}
                                  />
                                </Tooltip>
                                <Button
                                  size="sm" variant="outline" width="auto" height="fit-content"
                                  border="1px solid #DDDDDD" borderRadius="20px" padding="7px"
                                  onClick={(e) => { e.stopPropagation(); acceptSuggestion(item); }}
                                  _hover={{ backgroundColor: "#e8f5e9" }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="#208923" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </Button>
                                <IconButton size="sm" icon={
                                  <svg width="16" height="16" viewBox="0 0 16 16">
                                    <path d="M12 4L4 12M4 4L12 12" stroke="#E30F0F" strokeWidth="1.5" strokeLinecap="round" />
                                  </svg>
                                } variant="outline" onClick={() => rejectSuggestion(item)} borderRadius="full" />
                                <Button size="sm" variant="outline" colorScheme="blue" borderRadius="full" onClick={() => insertQuery(item)}>
                                  query
                                </Button>
                              </>
                            )}
                            {item.queryOnly && (
                              <>
                                <IconButton size="sm" icon={
                                  <svg width="16" height="16" viewBox="0 0 16 16">
                                    <path d="M12 4L4 12M4 4L12 12" stroke="#E30F0F" strokeWidth="1.5" strokeLinecap="round" />
                                  </svg>
                                } variant="outline" onClick={() => rejectSuggestion(item)} borderRadius="full" />
                                <Button size="sm" variant="outline" colorScheme="blue" borderRadius="full" onClick={() => insertQuery(item)}>
                                  query
                                </Button>
                              </>
                            )}
                          </HStack>
                        </Box>
                      );
                    };

                    if (items.length > VIRTUALIZE_THRESHOLD) {
                      return (
                        <>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            px="3" py="1"
                            bg="#f7f8fa"
                            borderBottom="1px solid #ebebeb"
                          >
                            <Text fontSize="11px" color="gray.400" fontWeight="500">
                              {items.length} suggestions
                            </Text>

                            <HStack gap="2px">
                              <Button
                                size="xs" variant="ghost" color="gray.500"
                                fontSize="11px" fontWeight="600" px="6px"
                                title="Jump to first suggestion in this group"
                                _hover={{ bg: "#eef2ff", color: "blue.500" }}
                                onClick={() => {
                                  groupListRefs.current[styleName]?.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                              >
                                ↑ Top
                              </Button>

                              <Button
                                size="xs" variant="ghost" color="gray.500"
                                fontSize="11px" fontWeight="600" px="6px"
                                title="Jump to last suggestion in this group"
                                _hover={{ bg: "#eef2ff", color: "blue.500" }}
                                onClick={() => {
                                  const el = groupListRefs.current[styleName];
                                  if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
                                }}
                              >
                                ↓ Bottom
                              </Button>

                              <Button
                                size="xs" variant="ghost" color="blue.500"
                                fontSize="11px" fontWeight="600" px="6px"
                                title="Scroll sidebar so this group's header is visible (then click to collapse)"
                                _hover={{ bg: "#eef2ff" }}
                                onClick={() => scrollToInContainer(groupHeaderRefs.current[styleName])}
                              >
                                ⤴ Header
                              </Button>
                            </HStack>
                          </Box>

                          {/* ── Virtual list: only ~10 items in DOM at any time ── */}
                          <VirtualGroupList
                            items={items}
                            renderItem={renderItem}
                            scrollToId={highlightedItemId}
                            onMount={(el) => { groupListRefs.current[styleName] = el; }}
                          />
                        </>
                      );
                    }

                    return items.map((item, i) => renderItem(item, i));

                  })()}
                </Box>
              </div>
            ))
          ) : (
            !props.isLoadingSuggestions && (
              <Box
                display="flex" alignItems="center" justifyContent="center"
                height="100%" flexDirection="column" gap="8px"
              >
                <img src={process.env.PUBLIC_URL + "/NewPages/Editor/NoSuggestions.png"} alt="Logo" />
                <Text fontSize="20px" fontWeight="500" color="rgba(0, 0, 0, 0.6)">No Suggestions</Text>
                <Text fontSize="14px" color="rgba(0, 0, 0, 0.4)">
                  Suggestions will appear here when available
                </Text>
              </Box>
            )
          )}
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state, ownProps) => ({
  autoSuggestions: state.autoSuggestion.suggestions,
  ischecked: state.autoSuggestion.ischecked,
  autoSuggestionsRejected: state.editToolRejected.autoSuggestionReject,
  ace: ownProps.ace,
  setTextFile: ownProps.setTextFile,
  pdfFull: ownProps.pdfFull,
  toggleEditor: ownProps.toggleEditor,
  showSuggestions: state.editToolRejected.showSuggestions,
  isLoadingSuggestions: state.editToolRejected.isLoadingSuggestion,
  clearAllTrigger: state.editToolRejected.clearAllTrigger,
});

const mapDispatchToProps = (dispatch) => ({
  addAutoSuggestion: (data) => dispatch({ type: "addAutoSuggestion", payload: data }),
  removeAutoSuggestion: (data) => dispatch({ type: "removeAutoSuggestion", payload: data }),
  clearAutoSuggestions: () => dispatch({ type: "clearAutoSuggestion" }),
  addAutoSuggestionReject: (data) => dispatch({ type: "AddAutoSuggestionReject", payload: data }),
  clearAutoSuggestionRejects: () => dispatch({ type: "EmptyAutoSuggestionReject" }),
  editToolAction: (data) => dispatch({ type: "EDITTOOL", payload: data }),
  toolStyles: (data) => dispatch({ type: "ToolSubmit", payload: data }),
  editToolRejected: () => dispatch({ type: "EmptyReject", payload: [] }),
  NoSuggestions: () => dispatch({ type: "Dontshowsuggestion" }),
  triggerClearAll: () => dispatch({ type: "triggerClearAll" }),
  removeStaleAutoSuggestions: (ids) => dispatch({ type: "removeStaleAutoSuggestions", payload: ids }),
  setPendingAIEdit: (data) => dispatch({ type: "setPendingAIEdit", payload: data }),
  uploadAutoSuggestions: (data) => dispatch({ type: "addAutoSuggestion", payload: data }),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditCorrections);